import axios from "axios";
import { toast } from "react-toastify";
import useAppStore from "../stores/appStore";
import { hasAuthToken } from "./authToken";

let reloadScheduled = false;
let connectivityCheckInFlight = false;

export const OFFLINE_WAIT_MESSAGE =
  "Please wait until you're back online.";

export const isBrowserOffline = (): boolean =>
  typeof navigator !== "undefined" && !navigator.onLine;

export const notifyOfflineActionBlocked = (): void => {
  toast.dismiss();
  toast.info(OFFLINE_WAIT_MESSAGE, {
    position: "top-center",
    autoClose: 3000,
    closeOnClick: false,
    pauseOnHover: true,
  });
};

export const hasAuthSession = (): boolean => hasAuthToken();

const isActiveQuizFlow = (): boolean => {
  const { isAssessmentOpen, isPreassessmentOpen } = useAppStore.getState();
  return isAssessmentOpen || isPreassessmentOpen;
};

export const resetConnectivityUiState = () => {
  reloadScheduled = false;
  connectivityCheckInFlight = false;
  const { setIsOnline, setShowReloadBanner } = useAppStore.getState();
  setIsOnline(navigator.onLine);
  setShowReloadBanner(false);
};

export const markAppOffline = () => {
  if (isActiveQuizFlow()) return;

  const {
    isOnline,
    isUserMenuModalOpen,
    setIsOnline,
    setShowReloadBanner,
  } = useAppStore.getState();
  if (isUserMenuModalOpen) return;
  if (!isOnline) return;

  reloadScheduled = false;
  setIsOnline(false);
  setShowReloadBanner(false);
};

export const scheduleAppReloadOnReconnect = () => {
  if (reloadScheduled || isActiveQuizFlow()) return;

  reloadScheduled = true;
  const { setIsOnline, setShowReloadBanner } = useAppStore.getState();
  setIsOnline(true);
  setShowReloadBanner(true);

  window.setTimeout(() => {
    window.location.reload();
  }, 1500);
};

export const notifySuccessfulApiResponse = () => {
  const { isOnline, showReloadBanner, setIsOnline, setShowReloadBanner } =
    useAppStore.getState();

  if (isActiveQuizFlow()) {
    if (!isOnline) {
      setIsOnline(true);
      setShowReloadBanner(false);
    }
    return;
  }

  if (!isOnline && !showReloadBanner) {
    scheduleAppReloadOnReconnect();
  }
};

export const checkConnectivityAndReload = async (): Promise<void> => {
  if (!hasAuthSession()) return;
  if (reloadScheduled || connectivityCheckInFlight || isActiveQuizFlow()) return;

  const { isOnline, showReloadBanner } = useAppStore.getState();
  if (isOnline || showReloadBanner) return;

  connectivityCheckInFlight = true;

  try {
    const { checkSessionStatus } = await import("../axios/user");
    await checkSessionStatus();
    scheduleAppReloadOnReconnect();
  } catch {
    // API still unreachable — stay on offline screen
  } finally {
    connectivityCheckInFlight = false;
  }
};

export const isAxiosCanceledError = (error: unknown): boolean => {
  if (axios.isCancel(error)) return true;
  return axios.isAxiosError(error) && error.code === "ERR_CANCELED";
};

export const isAxiosNetworkError = (error: unknown): boolean => {
  if (!axios.isAxiosError(error)) return false;
  if (isAxiosCanceledError(error)) return false;
  return !error.response;
};
